import { useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { authApiService } from '../services/authApiService';
import { debugWarn } from '../utils/debug';
import type { CurrentUserResponse, TeamBillingState, TeamModel } from '../types/auth';

export { TEAM_BILLING_READ_ONLY_EVENT, TEAM_BILLING_READ_ONLY_CODE, TEAM_BILLING_PATH } from '../utils/teamBillingReadOnly';

export interface TeamBillingStateInfo {
    /** Null when the team owes nothing (or BillingDunning is off, so the API sent nothing). */
    billingState: TeamBillingState | null;
    isPastDue: boolean;
    /** True only when the server would refuse writes for this user. Admin and Root are exempt. */
    isReadOnly: boolean;
    /** The outstanding payment waits for the customer to confirm it with their bank. */
    requiresAction: boolean;
    /** The user can pay from the team billing tab. */
    canManageBilling: boolean;
}

function isAdminOrRoot(role: string | undefined | null): boolean {
    return role === 'Admin' || role === 'Root';
}

/**
 * Who can pay for the team. The team billing tab has no role gate and the payment endpoints
 * accept any member of the team, so every signed-in member of a paying team can pay. Guest
 * teams do not pay.
 */
export function canManageTeamBilling(user: CurrentUserResponse | null | undefined): boolean {
    if (!user?.team?.teamId) return false;
    if ((user.team.teamType || '').toLowerCase() === 'guest') return false;
    return true;
}

/**
 * The team to store in auth state after loading it from team/get or team/update. Those payloads
 * carry no billingState; only /auth/me does. Replacing the team with them dropped the past-due
 * banner and read-only mode until the next full refresh, so the current billingState is kept.
 */
export function withSessionBillingState(next: TeamModel, current: TeamModel | undefined): TeamModel {
    if (!current || current.teamId !== next.teamId) return next;
    return { ...next, billingState: current.billingState };
}

/** Pure form of useTeamBillingState, for tests and non-React callers. */
export function resolveTeamBillingState(user: CurrentUserResponse | null | undefined): TeamBillingStateInfo {
    const raw = user?.team?.billingState ?? null;
    const status = raw?.status;
    const billingState = raw && (status === 'pastDue' || status === 'readOnly') ? raw : null;
    return {
        billingState,
        isPastDue: status === 'pastDue',
        isReadOnly: status === 'readOnly' && !isAdminOrRoot(user?.role),
        requiresAction: !!billingState && billingState.paymentStatus === 'requires_action',
        canManageBilling: canManageTeamBilling(user),
    };
}

/** The signed-in user's team billing state, from /auth/me. */
export function useTeamBillingState(): TeamBillingStateInfo {
    const { user } = useAuthContext();
    return resolveTeamBillingState(user);
}

/**
 * Re-reads /auth/me and replaces only the team in auth state. Unlike getCurrentUser() it does not
 * set the global loading flag, so an open case or page does not flash a spinner.
 */
export function useRefreshTeamBillingState(): () => Promise<void> {
    const { isAuthenticated, updateUserTeam } = useAuthContext();
    return useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await authApiService.getCurrentUser();
            const team = response.data?.team;
            if (team) updateUserTeam(() => team);
        } catch (err) {
            debugWarn('[useRefreshTeamBillingState] /auth/me failed', err);
        }
    }, [isAuthenticated, updateUserTeam]);
}
